import React from 'react';
import AccordionBlock from '@blocks/accordion-block/accordion-block';
import AlignedImageBlock from '@blocks/aligned-image-block/aligned-image-block';
import AutomatedListingTileBlock from '@blocks/automated-listing-tile-block/automated-listing-tile-block';
import CallToActionBlock from '@blocks/call-to-action-block/call-to-action-block';
import CircularExamplesBlock from '@blocks/circular-examples-block/circular-examples-block';
import CustomBlockHandler from '../custom-block-handler/custom-block-handler';
import EmbedBlock from '@blocks/embed-block/embed-block';
import FeaturedDownloadBlock from '@blocks/featured-download-block/featured-download-block';
import FlipCardBlock from '@blocks/flip-card-block/flip-card-block';
import HighlightBlock from '@blocks/highlight-block/highlight-block';
import ImageBlock from '@blocks/image-block/image-block';
import InteractiveImageBlock from '@blocks/interactive-image-block/interactive-image-block';
import LegendBlock from '@blocks/legend-block/legend-block';
import LogoBlock from '@blocks/logo-block/logo-block';
import MediaBlock from '@blocks/media-block/media-block';
import RichTextBlock from '@blocks/rich-text-block/rich-text-block';
import SectionLayout from '@layouts/section-layout';
import SingleCtaBlock from '@blocks/single-cta-block/single-cta-block';
import TabbedBlock from '@blocks/tabbed-block/tabbed-block';
import TileBlock from '@blocks/tile-block/tile-block';
import TwoColumnText from '@components/two-column-text/two-column-text';
import { InteractiveMap } from '@blocks/interactive-map/interactive-map';
import { FlexibleContentHandlerProps } from './flexible-content-handler.types';
import Separator from '@blocks/separator/separator';

const FlexibleContentHandler: React.FC<FlexibleContentHandlerProps> = ({
  data,
}) => {
  if (Array.isArray(data) && data.map) {
    return data.map((contentBlock, i) => {
      switch (contentBlock.type) {
        case 'alignedImageBlock':
          return (
            <SectionLayout
              key={`key${contentBlock.id}-${i}`}
              displaySeparator={false}
            >
              <AlignedImageBlock
                title={contentBlock.content.title}
                anchorId={contentBlock.content.anchorId}
                content={contentBlock.content.content}
                imageSrc={contentBlock.content.imageSrc}
                imageAlt={contentBlock.content.imageAlt}
                gatsbyImageData={contentBlock.content.gatsbyImageData}
                format={contentBlock.content.format}
                alignImage={contentBlock.content.alignImage}
                imageLink={contentBlock.content.imageLink}
                imageOpenInNewTab={contentBlock.content.imageOpenInNewTab}
                internalImageLink={contentBlock.content.internalImageLink}
                mediaUrl={contentBlock.content.mediaUrl}
                firstButtonLink={contentBlock.content.firstButtonLink}
                firstButtonText={contentBlock.content.firstButtonText}
                secondButtonText={contentBlock.content.secondButtonText}
                secondButtonLink={contentBlock.content.secondButtonLink}
              />
            </SectionLayout>
          );
        case 'callToActionBlock':
          return (
            <SectionLayout
              key={`key${contentBlock.id}-${i}`}
              displaySeparator={false}
            >
              <CallToActionBlock
                anchorId={contentBlock.content.anchorId}
                title={contentBlock.content.title}
                description={contentBlock.content.description}
                buttonText={contentBlock.content.buttonText}
                internalUrl={contentBlock.content.internalUrl}
                externalUrl={contentBlock.content.externalUrl}
                colour={contentBlock.content.colour}
                secondButtonText={contentBlock.content.secondButtonText}
                secondInternalUrl={contentBlock.content.secondInternalUrl}
                secondExternalUrl={contentBlock.content.secondExternalUrl}
              />
            </SectionLayout>
          );

        case 'circularExamplesBlock':
          return (
            <SectionLayout
              key={`key${contentBlock.id}-${i}`}
              displaySeparator={false}
            >
              <CircularExamplesBlock
                anchorId={contentBlock.content.anchorId}
                title={contentBlock.content.title}
                subtitle={contentBlock.content.subtitle}
                examples={contentBlock.content.examples}
                colour={contentBlock.content.colour}
              />
            </SectionLayout>
          );

        case 'embedBlock':
          return (
            <SectionLayout key={`key${contentBlock.id}-${i}`}>
              <EmbedBlock
                embedId={contentBlock.id}
                anchorId={contentBlock.content.anchorId}
                titleCmsOnly={contentBlock.content.titleCmsOnly}
                title={contentBlock.content.title}
                displayTitle={contentBlock.content.displayTitle}
                description={contentBlock.content.description}
                url={contentBlock.content.url}
                mobileHeight={contentBlock.content.mobileHeight}
                largeMobileHeight={contentBlock.content.largeMobileHeight}
                smallTabletHeight={contentBlock.content.smallTabletHeight}
                tabletHeight={contentBlock.content.tabletHeight}
                largeTabletHeight={contentBlock.content.largeTabletHeight}
                smallDesktopHeight={contentBlock.content.smallDesktopHeight}
                desktopHeight={contentBlock.content.desktopHeight}
              />
            </SectionLayout>
          );

        case 'textBlock':
          return (
            <SectionLayout
              key={`key${contentBlock.id}-${i}`}
              displaySeparator={false}
            >
              <RichTextBlock
                anchorId={contentBlock.content.anchorId}
                title={contentBlock.content.title}
                isFirstBlock={contentBlock.content.isFirstBlock}
                richTextData={contentBlock.content.richTextContent}
                tooltipData={contentBlock.content.tooltipData}
                backgroundColor={contentBlock.content.backgroundColor}
                backgroundImage={contentBlock.content.backgroundImage}
                mobileBackgroundImage={
                  contentBlock.content.mobileBackgroundImage
                }
                colour={contentBlock.content.colour}
                extraComponents={{
                  Accordion: AccordionBlock,
                  SingleCta: SingleCtaBlock,
                  AlignedImage: AlignedImageBlock,
                }}
              />
            </SectionLayout>
          );

        case 'highlightBlock':
          return (
            <SectionLayout
              key={`key${contentBlock.id}-${i}`}
              displaySeparator={false}
            >
              <HighlightBlock
                anchorId={contentBlock.content.anchorId}
                quote={contentBlock.content.quote}
                author={contentBlock.content.author}
                colour={contentBlock.content.colour}
                authorImage={contentBlock.content.authorImage}
                jobTitleOrganisation={contentBlock.content.jobTitleOrganisation}
                quoteTextColor={contentBlock.content.quoteTextColor}
                hasDottedLines={contentBlock.content.hasDottedLines}
                narrowWidth={contentBlock.content.narrowWidth}
              />
            </SectionLayout>
          );

        case 'downloadBlock':
          return (
            <SectionLayout key={`key${contentBlock.id}-${i}`}>
              <FeaturedDownloadBlock
                anchorId={contentBlock.content.anchorId}
                title={contentBlock.content.title}
                description={contentBlock.content.description}
                featuredBackgroundImageSrc={
                  contentBlock.content.backgroundImageUrl
                }
                gatsbyBackgroundImageData={contentBlock.content.gatsbyImageData}
                featuredBackgroundImageAlt={contentBlock.content.imageAlt}
                featuredBackgroundImageFormat={contentBlock.content.imageFormat}
                downloadTileData={contentBlock.content.downloads}
                colour={contentBlock.content.colour}
              />
            </SectionLayout>
          );

        case 'flipcardBlock':
          return (
            <SectionLayout key={`key${contentBlock.id}-${i}`}>
              <FlipCardBlock
                anchorId={contentBlock.content.anchorId}
                title={contentBlock.content.title}
                description={contentBlock.content.description}
                flipcardTiles={contentBlock.content.flipcardTiles}
                colour={contentBlock.content.colour}
                displayAsCarousel={contentBlock.content.displayAsCarousel}
              />
            </SectionLayout>
          );

        case 'imageBlock':
          return (
            <SectionLayout
              key={`key${contentBlock.id}-${i}`}
              displaySeparator={false}
            >
              <ImageBlock
                anchorId={contentBlock.content.anchorId}
                title={contentBlock.content.title}
                images={contentBlock.content.images}
                mobileImages={contentBlock.content.mobileImages}
                colour={contentBlock.content.colour}
                narrowWidth={contentBlock.content.narrowWidth}
                firstImageLink={contentBlock.content.firstImageLink}
                firstImageOpenInNewTab={
                  contentBlock.content.firstImageOpenInNewTab
                }
                secondImageLink={contentBlock.content.secondImageLink}
                secondImageOpenInNewTab={
                  contentBlock.content.secondImageOpenInNewTab
                }
              />
            </SectionLayout>
          );
        case 'interactiveImageBlock':
          return (
            <SectionLayout
              key={`key${contentBlock.id}-${i}`}
              displaySeparator={false}
            >
              <InteractiveImageBlock
                title={contentBlock.content.title}
                image={contentBlock.content.image}
                imageWidth={contentBlock.content.imageWidth}
                imageHeight={contentBlock.content.imageHeight}
                interactiveImagePinBlocks={
                  contentBlock.content.interactiveImagePinBlocks
                }
              />
            </SectionLayout>
          );

        case 'legendBlock':
          return (
            <SectionLayout
              key={`key${contentBlock.id}-${i}`}
              displaySeparator={false}
            >
              <LegendBlock
                logoSrc={contentBlock.content.logoSrc}
                companyName={contentBlock.content.companyName}
                origin={contentBlock.content.origin}
                founded={contentBlock.content.founded}
                website={contentBlock.content.website}
              />
            </SectionLayout>
          );

        case 'logoBlock':
          return (
            <SectionLayout key={`key${contentBlock.id}-${i}`}>
              <LogoBlock
                anchorId={contentBlock.content.anchorId}
                title={contentBlock.content.title}
                logos={contentBlock.content.logos}
                colour={contentBlock.content.colour}
                displayAsCarousel={contentBlock.content.displayAsCarousel}
              />
            </SectionLayout>
          );

        case 'tileBlock':
          return (
            <SectionLayout
              key={`key${contentBlock.id}-${i}`}
              displaySeparator={true}
              isDottedLine={contentBlock.content.hasDottedLines}
            >
              <TileBlock
                anchorId={contentBlock.content.anchorId}
                title={contentBlock.content.title}
                description={contentBlock.content.description}
                cta={contentBlock.content.cta}
                tiles={contentBlock.content.tiles}
                colour={contentBlock.content.colour}
                isCarousel={contentBlock.content.isCarousel}
                visibleTilesCount={contentBlock.content.visibleTilesCount}
              />
            </SectionLayout>
          );

        case 'automatedListingTileBlock':
          return (
            <SectionLayout
              key={`key${contentBlock.id}-${i}`}
              displaySeparator={true}
              isDottedLine={contentBlock.content.hasDottedLines}
            >
              <AutomatedListingTileBlock
                automatedListing={contentBlock.content.automatedListing}
                localeCode={contentBlock.content.localeCode}
                pageColour={contentBlock.content.pageColour}
                currentEntryId={
                  contentBlock.content.currentEntryId
                    ? contentBlock.content.currentEntryId
                    : null
                }
              />
            </SectionLayout>
          );

        case 'mediaBlock':
          return (
            <SectionLayout
              key={`key${contentBlock.id}-${i}`}
              displaySeparator={false}
            >
              <MediaBlock
                anchorId={contentBlock.content.anchorId}
                mediaSrc={contentBlock.content.mediaSrc}
              />
            </SectionLayout>
          );

        case 'accordionBlock':
          return (
            <SectionLayout
              key={`key${contentBlock.id}-${i}`}
              displaySeparator={false}
            >
              <AccordionBlock
                anchorId={contentBlock.content.anchorId}
                title={contentBlock.content.title}
                items={contentBlock.content.items}
                greyBackground={contentBlock.content.greyBackground}
              />
            </SectionLayout>
          );
        case 'customBlock':
          return (
            <SectionLayout
              key={`key${contentBlock.id}-${i}`}
              displaySeparator={false}
            >
              <CustomBlockHandler content={contentBlock.content} />
            </SectionLayout>
          );

        case 'singleCtaBlock':
          return (
            <SectionLayout
              key={`key${contentBlock.id}-${i}`}
              displaySeparator={false}
            >
              <SingleCtaBlock
                showWhiteBox={contentBlock.content.showWhiteBox}
                useLightText={contentBlock.content.useLightText}
                title={contentBlock.content.title}
                content={contentBlock.content.content}
                backgroundColor={contentBlock.content.backgroundColor}
                gatsbyImageData={contentBlock.content.gatsbyImageData}
                gatsbyMobileImageData={
                  contentBlock.content.gatsbyMobileImageData
                }
                format={contentBlock.content.format}
                mobileFormat={contentBlock.content.mobileFormat}
                imageSrc={contentBlock.content.imageSrc}
                mobileImageSrc={contentBlock.content.mobileImageSrc}
                imageAlt={contentBlock.content.imageAlt}
                alignment={contentBlock.content.alignment}
                linkOnePath={contentBlock.content.linkOnePath}
                linkOneText={contentBlock.content.linkOneText}
                isLinkOneExternal={contentBlock.content.isLinkOneExternal}
                linkTwoPath={contentBlock.content.linkTwoPath}
                linkTwoText={contentBlock.content.linkTwoText}
                isLinkTwoExternal={contentBlock.content.isLinkTwoExternal}
              />
            </SectionLayout>
          );

        case 'tabbedBlock':
          return (
            <SectionLayout
              key={`key${contentBlock.id}-${i}`}
              displaySeparator={false}
            >
              <TabbedBlock
                localeCode={contentBlock.content.localeCode}
                anchorId={contentBlock.content.anchorId}
                title={contentBlock.content.title}
                description={contentBlock.content.description}
                tabs={contentBlock.content.tabs}
                colour={contentBlock.content.colour}
                staticTranslations={contentBlock.content.staticTranslations}
                currentEntryId={contentBlock.content.currentEntryId || null}
              />
            </SectionLayout>
          );

        case 'twoColumnTextBlock':
          return (
            <SectionLayout
              key={`key${contentBlock.id}-${i}`}
              displaySeparator={false}
            >
              <TwoColumnText {...contentBlock.content} />
            </SectionLayout>
          );

        case 'separatorBlock':
          return (
            <SectionLayout
              key={`key${contentBlock.id}-${i}`}
              displaySeparator={false}
            >
              <Separator isFullWidth={contentBlock.content.isFullWidth} />
            </SectionLayout>
          );

        case 'interactiveMap':
          return (
            <SectionLayout
              key={`key${contentBlock.id}-${i}`}
              displaySeparator={false}
            >
              <InteractiveMap
                isFullWidth={contentBlock.content.isFullWidth}
                backgroundColor={contentBlock.content.backgroundColor}
                backgroundImage={contentBlock.content.backgroundImage}
                mobileBackgroundImage={
                  contentBlock.content.mobileBackgroundImage
                }
                interactiveImage={contentBlock.content.interactiveImage}
                pinContentMapper={contentBlock.content.pinContentMapper}
              />
            </SectionLayout>
          );

        default:
          console.warn(`Block type ${contentBlock.type} not found!`);
          return null;
      }
    });
  }
  return null;
};

export default FlexibleContentHandler;
